import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { X, Mail, Loader2, CheckCircle, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

import { BACKEND_URL_EXPORT as BACKEND_URL, API } from '../../config';

const InquiryForm = ({ vehicle = null, onClose }) => {
  const { customer, isAuthenticated, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    country: '',
    message: vehicle 
      ? `I am interested in the ${vehicle.year} ${vehicle.brand || ''} ${vehicle.model} (Stock: ${vehicle.stock_no}). Please provide more details.`
      : ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  // Auto-submit if customer is logged in
  useEffect(() => {
    if (isAuthenticated() && customer) {
      handleAutoSubmit();
    }
  }, []);

  const handleAutoSubmit = async () => {
    setLoading(true);
    try {
      const inquiryData = {
        name: customer.full_name,
        email: customer.email,
        phone: customer.phone || '',
        country: customer.country || '',
        message: vehicle 
          ? `I am interested in the ${vehicle.year} ${vehicle.brand || ''} ${vehicle.model} (Stock: ${vehicle.stock_no}). Please provide more details.`
          : '',
        vehicle_stock_no: vehicle?.stock_no || null,
        vehicle_model: vehicle?.model || null,
        vehicle_year: vehicle?.year || null
      };

      await axios.post(`${API}/public/inquiry`, inquiryData, { headers: getAuthHeaders() });
      
      setSuccess(true);
      toast.success('Inquiry sent successfully! We will contact you soon.');
      
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (error) {
      console.error('Error submitting inquiry:', error);
      toast.error('Failed to send inquiry. Please try again.');
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.name || !formData.email || !formData.country || !formData.message) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);

    try {
      const inquiryData = {
        ...formData,
        vehicle_stock_no: vehicle?.stock_no || null,
        vehicle_model: vehicle?.model || null,
        vehicle_year: vehicle?.year || null
      };

      await axios.post(`${API}/public/inquiry`, inquiryData);
      
      setSuccess(true);
      toast.success('Inquiry sent successfully! We will contact you soon.');
      
      // Close modal after 2 seconds
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (error) {
      console.error('Error submitting inquiry:', error);
      toast.error('Failed to send inquiry. Please try again or contact us directly.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 overflow-y-auto">
      <Card className="w-full max-w-2xl my-8">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-xl flex items-center">
              <Mail className="h-5 w-5 mr-2 text-red-600" />
              Send Inquiry
            </CardTitle>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="h-5 w-5" />
            </Button>
          </div>
          {vehicle && (
            <p className="text-sm text-gray-600 mt-2">
              Inquiring about: {vehicle.year} {vehicle.brand || ''} {vehicle.model} (Stock: {vehicle.stock_no})
            </p>
          )}
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-12">
              <Loader2 className="h-16 w-16 text-red-600 mx-auto mb-4 animate-spin" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Sending Inquiry...</h3>
              <p className="text-gray-600">Please wait while we process your request.</p>
            </div>
          ) : success ? (
            <div className="text-center py-8">
              <CheckCircle className="h-16 w-16 text-green-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Inquiry Sent!</h3>
              <p className="text-gray-600">
                Thank you for your inquiry. We will get back to you shortly.
              </p>
            </div>
          ) : !isAuthenticated() ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <UserPlus className="h-8 w-8 text-red-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Create Account to Send Inquiry</h3>
              <p className="text-gray-600 mb-6">
                Please create an account or login to send an inquiry. Track all your inquiries and orders in one place!
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button
                  onClick={() => {
                    onClose();
                    navigate('/customer/register');
                  }}
                  className="bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800"
                >
                  <UserPlus className="h-4 w-4 mr-2" />
                  Create Account
                </Button>
                <Button
                  onClick={() => {
                    onClose();
                    navigate('/customer/login');
                  }}
                  variant="outline"
                  className="border-red-600 text-red-600 hover:bg-red-50"
                >
                  Login to Existing Account
                </Button>
              </div>
            </div>
          ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium mb-1 block">
                    Name <span className="text-red-500">*</span>
                  </label>
                  <Input
                    name="name"
                    placeholder="Your full name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <Input
                    name="email"
                    type="email"
                    placeholder="your.email@example.com"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium mb-1 block">
                    Phone
                  </label>
                  <Input
                    name="phone"
                    type="tel"
                    placeholder="+1 234 567 8900"
                    value={formData.phone}
                    onChange={handleChange}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">
                    Country <span className="text-red-500">*</span>
                  </label>
                  <Input
                    name="country"
                    placeholder="Your country"
                    value={formData.country}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              <div>
                <label className="text-sm font-medium mb-1 block">
                  Message <span className="text-red-500">*</span>
                </label>
                <Textarea
                  name="message"
                  placeholder="Your message or inquiry..."
                  value={formData.message}
                  onChange={handleChange}
                  rows={6}
                  required
                />
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={onClose}
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={loading}
                  className="bg-red-600 hover:bg-red-700"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Mail className="h-4 w-4 mr-2" />
                      Send Inquiry
                    </>
                  )}
                </Button>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default InquiryForm;